import React, { useEffect, useMemo, useRef, useState } from 'react'
import './Psyduck.css'
import { Tooltip } from '@mui/material'
import PropTypes from 'prop-types'
import CatchingPokemonTwoToneIcon from '@mui/icons-material/CatchingPokemonTwoTone'
import useSound from 'common/hooks/useSound'
import soundCrying from 'assets/psyduck/crying.mp3'
import soundPanic from 'assets/psyduck/panic.mp3'
import soundPsy from 'assets/psyduck/psy.mp3'
import soundPsyiaia from 'assets/psyduck/psyiaia.mp3'
import soundPsypsyduck from 'assets/psyduck/psypsyduck.mp3'
import { shallowEqual, useSelector } from 'react-redux'
import clsx from 'clsx'

const WAITING = 'WAITING'
const ANGRYING = 'ANGRYING'
const DRAGGING = 'DRAGGING'
const POKING = 'POKING'

const PsyduckContainer = ({
    pos,
    setPos,
}) => {
    const isShiny = useMemo(() => Math.floor(Math.random() * 128) === 0, [])
    const [showBurst, setShowBurst] = useState(isShiny)

    useEffect(() => {
        if (isShiny) {
            const timer = setTimeout(() => setShowBurst(false), 1500)
            return () => clearTimeout(timer)
        }
    }, [isShiny])

    const { isMute } = useSelector(store => ({ isMute: store.app.isMute }), shallowEqual)
    const [playCrying, { stop: stopCrying }] = useSound(soundCrying, { loop: true })
    const [playPanic, { stop: stopPanic }] = useSound(soundPanic)
    const [playPsy, { stop: stopPsy }] = useSound(soundPsy)
    const [playPsyiaia, { stop: stopPsyiaia }] = useSound(soundPsyiaia)
    const [playPsypsyduck, { stop: stopPsypsyduck }] = useSound(soundPsypsyduck)

    useEffect(() => {
        if (isMute) return
        const interval = setInterval(() => {
            const rand = Math.random()
            if (rand < 0.01) {
                stopPsy()
                playPsy()
            } else if (rand < 0.02) {
                stopPsyiaia()
                playPsyiaia()
            } else if (rand < 0.03) {
                stopPsypsyduck()
                playPsypsyduck()
            }
        }, 10000)

        return () => {
            clearInterval(interval)
        }
    }, [isMute])

    const [state, setState] = useState(WAITING)
    const offset = useRef({ x: 0, y: 0 })

    const onMouseDown = (e) => {
        const div = e.currentTarget
        const rect = div.getBoundingClientRect()
        offset.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        }
        setState(POKING)
    }

    const onMouseUp = () => {
        if (state === POKING) {
            setState(ANGRYING)
            return
        }
        stopCrying()
        setState(WAITING)
    }

    useEffect(() => {
        if (state === POKING) {
            const startDrag = () => {
                if (!isMute) playCrying()
                setState(DRAGGING)
            }

            document.addEventListener('mousemove', startDrag)

            return () => {
                document.removeEventListener('mousemove', startDrag)
            }
        }
        if (state !== DRAGGING) return

        const movePsyduck = (e) => {
            setPos({
                x: e.clientX - offset.current.x,
                y: e.clientY - offset.current.y,
            })
        }

        document.addEventListener('mousemove', movePsyduck)

        return () => {
            stopCrying()
            document.removeEventListener('mousemove', movePsyduck)
        }
    }, [state])

    useEffect(() => {
        if (state !== ANGRYING) return
        stopPanic()
        if (!isMute) playPanic()
        const timeout = setTimeout(() => {
            setState(WAITING)
            stopPanic()
        }, 3000)

        return () => {
            clearTimeout(timeout)
        }
    }, [state])

    return (
        <div
            className='container'
            style={{
                left: `${pos.x}px`,
                top: `${pos.y}px`,
                zIndex: Math.round(pos.y) + 200,
            }}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
        >
            {isShiny && (
                <div className='shiny-stars'>
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className={`star star-${i+1}`} />
                    ))}
                </div>
            )}
            {showBurst && (
                <div className='shiny-burst'>
                    {[...Array(12)].map((_, i) => (
                        <div key={i} className='burst-star' />
                    ))}
                </div>
            )}

            <div className={clsx(['psyduck', state === DRAGGING && 'panic', isShiny && 'shiny-appearing'])}>
                <div className='foots'>
                    <div className={clsx(['left foot', state === DRAGGING && 'left-panic', isShiny && 'shiny'])} />
                    <div className={clsx(['right foot', state === DRAGGING && 'right-panic', isShiny && 'shiny'])} />
                </div>
                <div className={clsx(['body', state === WAITING && 'waiting', isShiny && 'shiny'])}>
                    <div className='arms'>
                        <div className={clsx(['left arm', state === WAITING && 'arm-waiting', state === ANGRYING && 'arm-angry', state === DRAGGING && 'arm-panic', isShiny && 'shiny'])}>
                            <div className={clsx(['finger left', isShiny && 'shiny'])} />
                            <div className={clsx(['finger right', isShiny && 'shiny'])} />
                        </div>
                        <div className={clsx(['right arm', state === WAITING && 'arm-waiting', state === ANGRYING && 'arm-angry', state === DRAGGING && 'arm-panic', isShiny && 'shiny'])}>
                            <div className={clsx(['finger left', isShiny && 'shiny'])} />
                            <div className={clsx(['finger right', isShiny && 'shiny'])} />
                        </div>
                    </div>
                </div>
                <div className={clsx(['hairs', state === WAITING && 'waiting'])}>
                    <div className={clsx(['left hair', state === WAITING && 'hair-waiting', state === DRAGGING && 'left-panic', isShiny && 'shiny'])} />
                    <div className={clsx(['middle hair', state === DRAGGING && 'middle-panic', isShiny && 'shiny'])} />
                    <div className={clsx(['right hair', state === WAITING && 'hair-waiting', state === DRAGGING && 'right-panic', isShiny && 'shiny'])} />
                </div>
                <div className={clsx(['head', state === WAITING && 'waiting', isShiny && 'shiny'])}>
                    <div className={clsx(['beak', isShiny && 'shiny'])}>
                        <div className={clsx(['nostrils', isShiny && 'shiny'])} />
                    </div>
                    <div className={clsx(['blush', isShiny && 'shiny'])} />
                    <div className='eyes blink'>
                        <div className='right eye' name='leftEye'>
                            <div className='pupil' />
                        </div>
                        <div className='left eye' name='rightEye'>
                            <div className='pupil' />
                        </div>
                    </div>
                </div>
            </div>
            <div className={`shadow ${state === DRAGGING ? 'panic' : ''}`} />
        </div>
    )
}

PsyduckContainer.propTypes = {
    pos: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
    }),
    setPos: PropTypes.func,
}

const vmin = Math.min(window.innerWidth, window.innerHeight)
const DEFAULT_PSYDUCK = [
    {
        x: window.innerWidth - vmin * 0.29,
        y: window.innerHeight - vmin * 0.38,
    },
    // {
    //     x: vmin * 0.29,
    //     y: vmin * 0.38,
    // },
]

const Psykokwalypse = () => {
    const [isDisplayed, setDisplay] = useState(false)
    const [psyducks, setPsyducks] = useState(DEFAULT_PSYDUCK)

    useEffect(() => {
        const leftEyes = document.getElementsByName('leftEye')
        const rightEyes = document.getElementsByName('rightEye')

        const moveEyes = (e) => {
            [...leftEyes, ...rightEyes].forEach(eye => {
                const eyeRect = eye.getBoundingClientRect()
                const pupil = eye.querySelector('.pupil')

                const eyeCenterX = eyeRect.left + eyeRect.width / 2
                const eyeCenterY = eyeRect.top + eyeRect.height / 2

                const angle = Math.atan2(
                    e.clientY - eyeCenterY,
                    e.clientX - eyeCenterX,
                )

                const distance = 1
                const x = Math.cos(angle) * distance
                const y = Math.sin(angle) * distance

                pupil.style.transform = `translate(${x}vmin, ${y}vmin) translate(-50%, -50%)`
            })
        }

        document.addEventListener('mousemove', moveEyes)

        return () => {
            document.removeEventListener('mousemove', moveEyes)
        }
    }, [])

    return (
        <>
            <Tooltip title='Afficher / Masquer les Psykokwaks'>
                <CatchingPokemonTwoToneIcon
                    sx={{
                        fontSize: '38px',
                        cursor: 'pointer',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                    }}
                    onClick={() => setDisplay(p => !p)}
                />
            </Tooltip>
            {isDisplayed && psyducks.map((pos, i) => (
                <PsyduckContainer
                    key={i}
                    pos={pos}
                    setPos={newPos => {
                        setPsyducks(prev => prev.map((p, pi) => pi === i ? newPos : p))
                    }}
                />
            ))}
        </>
    )
}

export default Psykokwalypse
